import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShieldCheck,
  Star,
  Building2,
  Mail,
  Key,
  DollarSign,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/awaiting-review — unified queue of every item sitting on the
 * admin's desk. Groups items by type (idol apps, sponsors, unread in-mail,
 * recovery cases, payouts) with a top count matching the Admin Inbox
 * widget. Each group deep-links to its dedicated review page.
 */
const AdminAwaitingReview = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get("tab") || "all");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/awaiting-review/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load review queue");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setActiveTab = (key) => {
    setTab(key);
    if (key === "all") searchParams.delete("tab");
    else searchParams.set("tab", key);
    setSearchParams(searchParams, { replace: true });
  };

  if (!user) return null;

  const counts = data?.counts || {};
  const total = data?.total || 0;

  const groups = [
    {
      key: "idol",
      title: "Idol Applications",
      count: counts.idol_applications || 0,
      icon: Star,
      color: "#F0C464",
      to: "/admin/idol-applications",
      items: (data?.idol_applications || []).slice(0, 6),
      renderItem: (a) => (
        <Link
          key={a.application_id}
          to="/admin/idol-applications"
          className="flex items-center justify-between p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#F0C464]/40 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold text-sm truncate">{a.stage_name || a.legal_name}</p>
            <p className="text-xs text-[#8A92A0] truncate">{a.email} · {a.country || "—"}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#8A92A0] flex-shrink-0 ml-2" />
        </Link>
      ),
    },
    {
      key: "sponsors",
      title: "Sponsors",
      count: counts.sponsors || 0,
      icon: Building2,
      color: "#32D583",
      to: "/admin/sponsors",
      items: (data?.sponsors || []).slice(0, 6),
      renderItem: (c) => (
        <Link
          key={c.corporate_id}
          to="/admin/sponsors"
          className="flex items-center justify-between p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#EC008C]/40 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold text-sm truncate">{c.company_name}</p>
            <p className="text-xs text-[#8A92A0] truncate">{c.contact_name} · {c.business_email}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#8A92A0] flex-shrink-0 ml-2" />
        </Link>
      ),
    },
    {
      key: "inmail",
      title: "Admin In-Mail (unread)",
      count: counts.admin_inmail_unread || 0,
      icon: Mail,
      color: "#EBB84C",
      to: "/dashboard/admin-inmail?status=unread",
      items: (data?.admin_inmail_unread || []).slice(0, 6),
      renderItem: (m) => (
        <Link
          key={m.inmail_id}
          to={`/dashboard/admin-inmail?source=${m.source || "all"}`}
          className="flex items-center justify-between p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#EBB84C]/40 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold text-sm truncate">{m.subject}</p>
            <p className="text-xs text-[#8A92A0] truncate">{m.from_name} · {m.from_email}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#8A92A0] flex-shrink-0 ml-2" />
        </Link>
      ),
    },
    {
      key: "recovery",
      title: "Recovery Cases",
      count: counts.recovery_cases || 0,
      icon: Key,
      color: "#C084FC",
      to: "/dashboard/admin",
      items: (data?.recovery_cases || []).slice(0, 6),
      renderItem: (r) => (
        <Link
          key={r.case_id}
          to="/dashboard/admin"
          className="flex items-center justify-between p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#FF6BC5]/40 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold text-sm truncate">{r.email}</p>
            <p className="text-xs text-[#8A92A0] truncate">{(r.reason || "").slice(0, 80)}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#8A92A0] flex-shrink-0 ml-2" />
        </Link>
      ),
    },
    {
      key: "payouts",
      title: "Payouts",
      count: counts.payouts || 0,
      icon: DollarSign,
      color: "#86EFAC",
      to: "/admin/payouts",
      items: (data?.payouts || []).slice(0, 6),
      renderItem: (p) => (
        <Link
          key={p.payout_id}
          to="/admin/payouts"
          className="flex items-center justify-between p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#8DB87A]/40 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold text-sm truncate">{p.artist_name || p.artist_id}</p>
            <p className="text-xs text-[#8A92A0] truncate">${((p.amount_cents || 0) / 100).toFixed(2)} · {p.status}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#8A92A0] flex-shrink-0 ml-2" />
        </Link>
      ),
    },
  ];

  const filtered = tab === "all" ? groups : groups.filter((g) => g.key === tab);

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-awaiting-review-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center"
            data-testid="awaiting-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">Admin · Awaiting Review</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#EC008C]" />
            <span className="text-sm text-[#8A92A0]" data-testid="awaiting-total">{total} total</span>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Awaiting review</h1>
          <p className="text-[#8A92A0] max-w-2xl">
            Everything sitting on your desk right now — pending idol applications, sponsors, unread in-mail, recovery cases, and payouts. Click any group to jump to its dedicated review page.
          </p>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="awaiting-filter-row">
          {[
            { key: "all", label: `All · ${total}` },
            ...groups.map((g) => ({ key: g.key, label: `${g.title} · ${g.count}` })),
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveTab(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                tab === f.key
                  ? "bg-[#EC008C] text-black"
                  : "bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:text-white"
              }`}
              data-testid={`awaiting-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#EC008C]" /></div>
        ) : total === 0 ? (
          <div className="card p-16 text-center" data-testid="awaiting-empty">
            <ShieldCheck className="w-12 h-12 text-[#8DB87A] mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Inbox zero 🎉</h3>
            <p className="text-[#8A92A0] text-sm">Nothing waiting for review right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filtered.map((g) => (
              <div key={g.key} className="card p-5" data-testid={`awaiting-group-${g.key}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${g.color}22`, border: `1px solid ${g.color}55` }}
                    >
                      <g.icon className="w-5 h-5" style={{ color: g.color }} />
                    </div>
                    <div>
                      <p className="font-['Unbounded'] text-white font-bold">{g.title}</p>
                      <p className="text-xs text-[#8A92A0]">{g.count} awaiting</p>
                    </div>
                  </div>
                  <Link
                    to={g.to}
                    className="flex items-center gap-1 text-xs font-bold hover:underline"
                    style={{ color: g.color }}
                    data-testid={`awaiting-group-cta-${g.key}`}
                  >
                    Open <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                {g.count === 0 ? (
                  <p className="text-xs text-[#4A5260] italic">Nothing here.</p>
                ) : (
                  <div className="space-y-2">
                    {g.items.map(g.renderItem)}
                    {g.count > g.items.length && (
                      <p className="text-[10px] text-[#4A5260] text-center pt-1">+ {g.count - g.items.length} more…</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAwaitingReview;
